import React from 'react';
import { Icon } from '@iconify/react';
import { DropdownMenu } from '../Button/Button';

/**
 * Card Component - 統一卡片樣式
 *
 * 用於 TrashPage, LibraryPage, ProjectsPage 等頁面
 * 支援：預覽區、資訊區、選單按鈕、hover 效果
 */

/**
 * Card variants for different use cases
 */
const cardVariants = {
  // 標準卡片 (TrashPage, ProjectsPage)
  default: 'rounded-xl p-5',
  // 媒體卡片 (LibraryPage) - 無內padding，preview佔滿
  media: 'rounded-xl overflow-hidden',
  // 設定卡片 (SettingsPage)
  settings: 'rounded-xl p-6',
  // 點數卡片 (CreditsPage)
  credits: 'rounded-2xl p-6',
};

/**
 * Main Card Container
 */
export function Card({
  children,
  variant = 'default',
  interactive = false,
  selected = false,
  className = '',
  onClick,
  ...props
}) {
  const baseClasses = 'relative transition-all duration-200 bg-[var(--bg-secondary)] border border-[var(--border-color)]';
  const interactiveClasses = interactive ? 'cursor-pointer hover:-translate-y-1 hover:shadow-lg' : '';
  const selectedClasses = selected ? 'ring-2 ring-blue-500' : '';

  return (
    <div
      className={`${baseClasses} ${cardVariants[variant] || cardVariants.default} ${interactiveClasses} ${selectedClasses} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card Preview Area - 預覽圖/縮圖區
 */
export function CardPreview({
  children,
  height = 'h-[120px]',
  className = '',
  onClick,
  ...props
}) {
  return (
    <div
      className={`w-full ${height} rounded-lg overflow-hidden flex items-center justify-center bg-[var(--bg-primary)] ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card Image - 預覽圖片
 */
export function CardImage({
  src,
  alt = '',
  fallbackIcon = 'mdi:image',
  className = '',
}) {
  if (!src) {
    return (
      <Icon
        icon={fallbackIcon}
        className="text-5xl text-[var(--text-tertiary)]"
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`w-full h-full object-cover ${className}`}
    />
  );
}

/**
 * Card Content Area - 資訊區
 */
export function CardContent({
  children,
  className = '',
  ...props
}) {
  return (
    <div className={`${className}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Card Title
 */
export function CardTitle({
  children,
  className = '',
  truncate = true,
  ...props
}) {
  const truncateClasses = truncate ? 'overflow-hidden text-ellipsis whitespace-nowrap' : '';

  return (
    <h3
      className={`m-0 text-base font-semibold text-[var(--text-primary)] ${truncateClasses} ${className}`}
      {...props}
    >
      {children}
    </h3>
  );
}

/**
 * Card Description / Subtitle
 */
export function CardDescription({
  children,
  className = '',
  truncate = false,
  ...props
}) {
  const truncateClasses = truncate ? 'overflow-hidden text-ellipsis whitespace-nowrap' : '';

  return (
    <p
      className={`m-0 text-sm text-[var(--text-tertiary)] ${truncateClasses} ${className}`}
      {...props}
    >
      {children}
    </p>
  );
}

/**
 * Card Menu - 右上角選單按鈕
 */
export function CardMenu({
  options,
  position = 'top-4 right-4',
  className = '',
}) {
  return (
    <div
      className={`absolute ${position} z-10 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu
        icon="mdi:dots-vertical"
        size="sm"
        options={options}
      />
    </div>
  );
}

/**
 * Card Badge - 標籤/狀態
 */
export function CardBadge({
  children,
  variant = 'default',
  icon,
  className = '',
}) {
  const badgeVariants = {
    default: 'bg-[var(--bg-primary)] text-[var(--text-secondary)]',
    warning: 'bg-red-100 text-red-600',
    success: 'bg-green-100 text-green-600',
    info: 'bg-blue-100 text-blue-600',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 py-1 px-2.5 rounded-md text-xs font-medium ${badgeVariants[variant] || badgeVariants.default} ${className}`}
    >
      {icon && <Icon icon={icon} className="text-sm" />}
      {children}
    </span>
  );
}

/**
 * Card Footer - 底部區域（動作按鈕等）
 */
export function CardFooter({
  children,
  className = '',
  ...props
}) {
  return (
    <div
      className={`mt-4 pt-4 border-t border-[var(--border-color)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Media Card - LibraryPage 專用的媒體卡片
 * 結合 Card + CardPreview + CardContent
 */
export function MediaCard({
  src,
  type,
  filename,
  size,
  onClick,
  onDelete,
  onCopy,
  onOpen,
  menuOptions,
  t = (key) => key, // i18n function
}) {
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return 'mdi:image';
    if (fileType?.startsWith('video/')) return 'mdi:video';
    if (fileType?.includes('pdf')) return 'mdi:file-pdf';
    return 'mdi:file';
  };

  const defaultMenuOptions = [
    {
      label: t('libraryPage.open'),
      icon: 'mdi:open-in-new',
      onClick: onOpen,
    },
    {
      label: t('libraryPage.copyUrl'),
      icon: 'mdi:content-copy',
      onClick: onCopy,
    },
    { divider: true },
    {
      label: t('libraryPage.delete'),
      icon: 'mdi:delete',
      danger: true,
      onClick: onDelete,
    },
  ];

  return (
    <Card variant="media" interactive onClick={onClick}>
      {/* Menu */}
      <CardMenu
        position="top-2 right-2"
        options={menuOptions || defaultMenuOptions}
      />

      {/* Preview */}
      <CardPreview height="h-40" className="rounded-none">
        <CardImage
          src={type?.startsWith('image/') ? src : null}
          fallbackIcon={getFileIcon(type)}
        />
      </CardPreview>

      {/* Info */}
      <CardContent className="p-3">
        <CardTitle className="text-[13px] mb-1">
          {filename || t('libraryPage.untitled')}
        </CardTitle>
        <CardDescription className="text-xs">
          {size}
        </CardDescription>
      </CardContent>
    </Card>
  );
}

/**
 * Project Card - TrashPage, ProjectsPage 專用
 */
export function ProjectCard({
  title,
  description,
  previewContent,
  badge,
  badgeVariant = 'default',
  badgeIcon,
  onClick,
  menuOptions,
  className = '',
}) {
  return (
    <Card variant="default" interactive onClick={onClick} className={className}>
      {/* Menu */}
      {menuOptions && (
        <CardMenu options={menuOptions} />
      )}

      {/* Preview */}
      <CardPreview className="mb-4">
        {previewContent}
      </CardPreview>

      {/* Info */}
      <CardContent>
        <CardTitle className="mb-2">{title}</CardTitle>
        {description && (
          <CardDescription className="mb-3">{description}</CardDescription>
        )}
        {badge && (
          <CardBadge variant={badgeVariant} icon={badgeIcon}>
            {badge}
          </CardBadge>
        )}
      </CardContent>
    </Card>
  );
}

export default Card;
